import React from 'react';

import EvaluateScoreSkill from './evaluate-score-skill.component';
import EvaluateTimedSkill from './evaluate-timed-skill.component';
import EvaluateNumberSkill from './evaluate-number-skill.component';
import { SkillMeasurementType } from '../../../lib/types';

interface IRenderSkillWidgetProps {
  skill: ISkill;
  // Widgets read only `score.raw`; a full IScore only flows in from the Evaluate scorers.
  score?: Pick<IScore, 'raw'>;
  pending?: boolean;
  roundNum: number;
  // `type` is 'TIME' for TIMED, else 'NUMBER'; `advance` is true for one-tap saves
  // (SCORE/TIMED) and false for the per-keystroke number field (safe auto-advance).
  onSave: (raw: string, type: string, advance: boolean) => void;
  // Carries the score the widget forwards on remove (calibration ignores it and builds
  // its own op); typed — not `() => void` — so a refactor can't silently drop the arg.
  onRemove: (score?: IScore) => void;
}

// Single owner of the skill-type → Evaluate widget switch, shared by the composite,
// per-player, and calibration scorers; each supplies its own onSave/onRemove.
export const RenderSkillWidget: React.FC<IRenderSkillWidgetProps> = ({ skill, score, pending, roundNum, onSave, onRemove }) => {
  // The legacy widgets type `score` as IScore but read only `.raw`; bridge the narrow
  // prop to their type in one place rather than fabricating a full entity at the source.
  const widgetScore = score as IScore | undefined;

  if (skill.type === SkillMeasurementType.TIMED) {
    return (
      <EvaluateTimedSkill
        key={skill._id}
        roundNum={roundNum}
        score={widgetScore}
        pending={pending}
        removeScore={onRemove}
        saveScore={(value: number) => onSave(String(value), 'TIME', true)}
      />
    );
  }

  if (skill.type === SkillMeasurementType.SCORE) {
    return (
      <EvaluateScoreSkill
        key={skill._id}
        skill={skill}
        score={widgetScore}
        pending={pending}
        removeScore={onRemove}
        saveScore={(value: string) => onSave(String(value), 'NUMBER', true)}
      />
    );
  }

  if (skill.type === SkillMeasurementType.INTEGER || skill.type === SkillMeasurementType.FLOAT) {
    return (
      <EvaluateNumberSkill
        key={skill._id}
        allowedDecimals={skill.type === SkillMeasurementType.FLOAT ? 2 : undefined}
        skill={skill}
        score={widgetScore}
        pending={pending}
        removeScore={onRemove}
        saveScore={(value: string) => onSave(String(value), 'NUMBER', false)}
      />
    );
  }

  return null;
};
